export function bigNumberTransform(value) {
    if (value === undefined || value === null) return '';

    const digits = countIntegerDigits(value);
    const units = [
        { minDigits: 16, unit: '千兆', factor: 1e15 },
        { minDigits: 13, unit: '兆', factor: 1e12 },
        { minDigits: 9, unit: '亿', factor: 1e8 },
        { minDigits: 5, unit: '万', factor: 1e4 },
    ];

    let [num, unit] = [value, ''];

    // 倒序查找第一个满足条件的单位
    for (const { minDigits, unit: u, factor } of units) {
        if (digits >= minDigits) {
            const isExact = value % factor === 0;
            num = isExact ? value / factor : (value / factor).toFixed(2);
            num = String(num).replace(/\.?0+$/, '');
            unit = u;
            break;
        }
    }

    return num + unit;
}

function countIntegerDigits(value) {
    const integerPart = Math.trunc(value);
    return Math.abs(integerPart).toString().length;
}

/**
 * 格式化活动开放时刻，如「9月25日 12:00」。
 * 强制按上海时区展示：服务端活动起止时间以游戏服务器（Asia/Shanghai）为准，
 * 避免访客浏览器处于其他时区时把开放时间显示成错误的钟点。
 * 用 hourCycle:'h23' 而非 hour12:false：后者在部分引擎下会把 0 点渲染成「24:00」，
 * 而活动开放/截止时刻常常正好是 0 点；同时给出 hour12 时 Intl 会忽略 hourCycle，故只保留前者。
 * @param ts 秒级时间戳，空值返回空串
 * @returns 可读时间文本
 */
export function formatOpenTime(ts) {
    if (!ts) return '';

    return new Date(ts * 1000).toLocaleString('zh-CN', {
        timeZone: 'Asia/Shanghai',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    });
}

/**
 * 预设阵容槽位ID的防御性上界。
 * 游戏里角色最多拥有 6 个预设阵容（VIP 升到顶给 4 个，其余靠活动兑换“布阵槽位”），
 * 该上界只用于兜住异常数据（users.role_info 是 JSON 列，历史上有被损坏成非预期值的先例），
 * 避免一个异常巨大的 id/maxTeamId 生成超大数组并被展开成上百万个 Radio 而拖垮页面。
 * @type {number}
 */
const MAX_PRESET_TEAM_ID = 6;

/**
 * 列出角色实际可用的预设阵容槽位。
 * VIP 等级只决定免费解锁的槽位数下限（1~4个），角色通过活动兑换“布阵槽位”后可以获得更多预设阵容，
 * 因此槽位上限以角色真实数据（role_info.presetTeamInfo / role_info.maxTeamId）为准，与后端
 * Role.vipTeamSlotCount（下限）+ Role.maxTeamId（实际）的判定口径保持一致。
 * @param roleInfo 角色信息，即 user.role_info
 * @returns 从1开始连续编号的槽位数组，元素为 {id, name}；name 为玩家自定义名称，未命名时为空串
 */
export function listPresetTeamSlots(roleInfo) {
    const vip = roleInfo?.vip || 0;
    // VIP 提供的槽位数下限
    let maxId = 1;
    if (vip > 0) maxId = 2;
    if (vip >= 8) maxId = 3;
    if (vip >= 14) maxId = 4;

    // 收集角色已有的预设阵容名称，并把槽位上限抬升到角色实际拥有的数量
    const nameMap = {};
    const presetTeamInfo = roleInfo?.presetTeamInfo;
    if (Array.isArray(presetTeamInfo)) {
        for (const team of presetTeamInfo) {
            const id = Number(team?.id);
            if (!Number.isInteger(id) || id <= 0) continue;
            if (id > maxId) maxId = Math.min(id, MAX_PRESET_TEAM_ID);
            if (team.name) nameMap[id] = String(team.name);
        }
    }

    // 服务端记录的最大可用阵容ID，兜住 presetTeamInfo 尚未落库的情况
    const maxTeamId = Number(roleInfo?.maxTeamId);
    if (Number.isInteger(maxTeamId) && maxTeamId > maxId) maxId = Math.min(maxTeamId, MAX_PRESET_TEAM_ID);

    const slots = [];
    for (let id = 1; id <= maxId; id++) {
        slots.push({ id, name: nameMap[id] || '' });
    }
    return slots;
}

/**
 * 刷新页面，正确处理URL中的time参数和hash锚点
 * - 移除hash锚点
 * - 更新现有的time参数或添加新的time参数
 */
export function refreshPageWithTimestamp() {
    const url = new URL(window.location.href);
    // 移除hash锚点
    url.hash = '';
    // 更新或添加time参数
    url.searchParams.set('time', Date.now().toString());
    window.location.href = url.toString();
}
